/**
 * Field Structure Storage Mechanism
 * 
 * This utility provides functions to store, retrieve, and manipulate
 * dynamic field structures in the ticket creation workflow.
 */

import { getFieldCategory, getFieldType } from './fieldPatternUtils';
import { CONTEXT_TYPES } from './fieldContextUtils';

/**
 * Creates a new field structure object
 * 
 * @param {String} serviceId - ID of the selected service
 * @returns {Object} - Empty field structure
 */
export const createFieldStructure = (serviceId) => {
  return {
    serviceId,
    categories: {},
    fieldProperties: {},
    timestamp: Date.now(),
    version: 1
  };
};

/**
 * Deep clones a field structure to prevent mutation
 * 
 * @param {Object} structure - Field structure to clone
 * @returns {Object} - Cloned field structure
 */
export const cloneFieldStructure = (structure) => {
  if (!structure) return null;
  return JSON.parse(JSON.stringify(structure));
};

/**
 * Adds a field to a field structure
 * 
 * @param {Object} structure - Field structure to modify
 * @param {String} fieldName - Name of the field to add
 * @param {Object} fieldProps - Field properties
 * @returns {Object} - Updated field structure (new object)
 */
export const addFieldToStructure = (structure, fieldName, fieldProps = {}) => {
  // Clone the structure to prevent mutation
  const newStructure = cloneFieldStructure(structure);
  
  // Determine field category if not provided
  const category = fieldProps.category || getFieldCategory(fieldName);
  
  // Determine field type if not provided
  const fieldType = fieldProps.type || getFieldType(fieldName);
  
  // Initialize category if it doesn't exist
  if (!newStructure.categories[category]) {
    newStructure.categories[category] = [];
  }
  
  // Add field to category if not already present
  if (!newStructure.categories[category].includes(fieldName)) {
    newStructure.categories[category].push(fieldName);
  }
  
  // Add field properties
  newStructure.fieldProperties[fieldName] = {
    ...fieldProps,
    type: fieldType,
    category
  };
  
  // Update version and timestamp
  newStructure.version += 1;
  newStructure.timestamp = Date.now();
  
  return newStructure;
};

/**
 * Processes an array of fields and adds them to the structure
 * 
 * @param {Object} structure - Field structure to modify
 * @param {Array} fields - Array of field objects or field names
 * @returns {Object} - Updated field structure (new object)
 */
export const processFieldsIntoStructure = (structure, fields) => {
  if (!fields || !Array.isArray(fields)) return structure;
  
  let updatedStructure = cloneFieldStructure(structure);
  
  fields.forEach(field => {
    // Handle both field objects and field names
    if (typeof field === 'string') {
      updatedStructure = addFieldToStructure(updatedStructure, field);
    } else {
      // For workflow definition fields, use code as name but preserve title
      const fieldName = field.name || field.code;
      
      // Create a field properties object that preserves all original properties
      // including title, which is crucial for proper label display
      const fieldProps = {
        ...field,
        // Ensure title is preserved for UI display
        title: field.title || field.displayName
      };
      
      updatedStructure = addFieldToStructure(
        updatedStructure, 
        fieldName, 
        fieldProps
      );
    }
  });
  
  return updatedStructure;
};

/**
 * Creates a field structure from service response
 * 
 * @param {Object} serviceResponse - Response from service selection API
 * @param {String} serviceId - ID of the selected service
 * @returns {Object} - Field structure
 */
export const createStructureFromServiceResponse = (serviceResponse, serviceId) => {
  // Create empty structure
  const structure = createFieldStructure(serviceId);
  
  // Process static fields
  if (serviceResponse.staticFields && Array.isArray(serviceResponse.staticFields)) {
    // Process static fields
    let updatedStructure = processFieldsIntoStructure(structure, serviceResponse.staticFields);
    
    // Process dynamic fields from workflow definition if available
    if (serviceResponse.workflowDefinition && 
        serviceResponse.workflowDefinition.steps && 
        Array.isArray(serviceResponse.workflowDefinition.steps)) {
      
      // Extract fields from all workflow steps
      const workflowFields = [];
      serviceResponse.workflowDefinition.steps.forEach(step => {
        if (step.fields && Array.isArray(step.fields)) {
          // Ensure each field has both code and title preserved
          step.fields.forEach(field => {
            workflowFields.push({
              ...field,
              // Ensure name is set to code for consistency
              name: field.code,
              // Preserve title for display
              title: field.title || field.displayName
            });
          });
        }
      });
      
      // Process workflow fields
      if (workflowFields.length > 0) {
        updatedStructure = processFieldsIntoStructure(updatedStructure, workflowFields);
      }
    }
    
    return updatedStructure;
  }
  
  return structure;
};

/**
 * Updates field structure for a specific context
 * 
 * @param {Object} structure - Field structure to update
 * @param {String} context - Context identifier from CONTEXT_TYPES
 * @returns {Object} - Updated field structure for the context
 */
export const updateStructureForContext = (structure, context) => {
  if (!structure) return null;
  
  const newStructure = cloneFieldStructure(structure);
  
  // If employee is selected, update document field categories
  if (context === CONTEXT_TYPES.EMPLOYEE_SELECTED) {
    // Find document fields
    const documentFields = Object.keys(newStructure.fieldProperties)
      .filter(fieldName => {
        const props = newStructure.fieldProperties[fieldName];
        return props.type === 'FILE' || props.category === 'DOCUMENTS';
      });
    
    // Move document fields to DOCUMENTS category
    documentFields.forEach(fieldName => {
      // Remove from current category
      Object.keys(newStructure.categories).forEach(category => {
        newStructure.categories[category] = newStructure.categories[category]
          .filter(field => field !== fieldName);
      });
      
      // Add to DOCUMENTS category
      if (!newStructure.categories.DOCUMENTS) {
        newStructure.categories.DOCUMENTS = [];
      }
      
      if (!newStructure.categories.DOCUMENTS.includes(fieldName)) {
        newStructure.categories.DOCUMENTS.push(fieldName);
      }
      
      // Update field properties
      newStructure.fieldProperties[fieldName] = {
        ...newStructure.fieldProperties[fieldName],
        category: 'DOCUMENTS'
      };
    });
  }
  
  return newStructure;
};

/**
 * Singleton instance for storing the current field structure
 */
let currentFieldStructure = null;
let currentContext = CONTEXT_TYPES.DEFAULT;

/**
 * Stores the field structure in memory
 * 
 * @param {Object} structure - Field structure to store
 */
export const storeFieldStructure = (structure) => {
  currentFieldStructure = cloneFieldStructure(structure);
};

/**
 * Retrieves the stored field structure
 * 
 * @returns {Object} - Stored field structure
 */
export const getStoredFieldStructure = () => {
  return cloneFieldStructure(currentFieldStructure);
};

/**
 * Sets the current context
 * 
 * @param {String} context - Context identifier from CONTEXT_TYPES
 */
export const setCurrentContext = (context) => {
  currentContext = context;
};

/**
 * Gets the current context
 * 
 * @returns {String} - Current context
 */
export const getCurrentContext = () => {
  return currentContext;
};

/**
 * Gets the field structure for the current context
 * 
 * @returns {Object} - Field structure for the current context
 */
export const getContextualFieldStructure = () => {
  return updateStructureForContext(currentFieldStructure, currentContext);
};
